/**
 * Users page — per docs/specs/users.md
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Search, Users as UsersIcon } from 'lucide-react';
import { Chip } from '../../components/chip';
import { useToast } from '../../components/toast';
import { useShell } from '../../layout/shell';
import { UserDetailPanel } from './user-detail-panel';
import { useUserSegmentCounts, useUsers } from '../../../lib/api';
import type { AdminUser, UserSegment } from '../../../lib/types';
import { exportCsv } from '../../../lib/csv';

const SEGMENTS: { id: UserSegment; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'new', label: 'New' },
    { id: 'unconfirmed', label: 'Unconfirmed email' },
    { id: 'abandoned', label: 'Abandoned onboarding' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
    { id: 'paying', label: 'Paying' },
    { id: 'pending_legal', label: 'Pending latest terms' },
    { id: 'suspended', label: 'Suspended' },
    { id: 'deleted', label: 'Deleted' }
];

const fmtMoney = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtStorage = (gb: number) => `${gb.toFixed(1)} GB`;
function parseDateTs(value?: string | null) {
    if (!value) return Number.NaN;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : Number.NaN;
}

function compareUsers(a: AdminUser, b: AdminUser, sortKey: string, sortDir: 'asc' | 'desc') {
    const direction = sortDir === 'asc' ? 1 : -1;
    const compareNumbers = (left: number | null | undefined, right: number | null | undefined) => {
        const leftMissing = left == null || Number.isNaN(left);
        const rightMissing = right == null || Number.isNaN(right);
        if (leftMissing && rightMissing) return 0;
        if (leftMissing) return 1;
        if (rightMissing) return -1;
        return direction * ((left as number) - (right as number));
    };
    const compareDates = (left?: string | null, right?: string | null) =>
        compareNumbers(parseDateTs(left), parseDateTs(right));

    switch (sortKey) {
        case 'revenue_lifetime':
            return compareNumbers(a.revenue_lifetime, b.revenue_lifetime);
        case 'gross_profit':
            return compareNumbers(a.gross_profit, b.gross_profit);
        case 'tracks':
            return compareNumbers(a.tracks, b.tracks);
        case 'storage_gb':
            return compareNumbers(a.storage_gb, b.storage_gb);
        case 'last_active':
            return compareDates(a.last_active_at, b.last_active_at);
        case 'signed_up':
            return compareDates(a.signed_up_at, b.signed_up_at);
        default:
            return direction * a.name.localeCompare(b.name);
    }
}

const planTone = (plan: AdminUser['plan']) =>
    plan === 'studio' || plan === 'team'
        ? 'purple'
        : plan === 'pro'
          ? 'info'
          : plan === 'trial'
            ? 'warning'
            : 'muted';
const statusTone = (status: AdminUser['status']) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'new':
            return 'info';
        case 'unconfirmed':
        case 'abandoned':
        case 'inactive':
            return 'warning';
        case 'suspended':
        case 'deleted':
            return 'danger';
        default:
            return 'muted';
    }
};

export function UsersPage() {
    const [params, setParams] = useSearchParams();
    const segment = (params.get('segment') as UserSegment) || 'all';
    const search = params.get('q') || '';
    const plan = params.get('plan') || 'all';
    const appVersion = params.get('v') || 'all';
    const sort = params.get('sort') || 'revenue_lifetime:desc';

    const [searchInput, setSearchInput] = useState(search);
    useEffect(() => setSearchInput(search), [search]);

    const counts = useUserSegmentCounts();
    const filters = useMemo(
        () => ({ segment, search, plan, appVersion, sort }),
        [segment, search, plan, appVersion, sort]
    );
    const users = useUsers(filters);
    const visibleUsers = useMemo(() => {
        const [sortKey, sortDirRaw] = sort.split(':');
        const sortDir = sortDirRaw === 'asc' ? 'asc' : 'desc';
        return [...(users.data ?? [])].sort((a, b) => compareUsers(a, b, sortKey, sortDir));
    }, [sort, users.data]);

    const { setDetailPanel } = useShell();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const { toast } = useToast();

    useEffect(() => {
        if (selectedId) {
            setDetailPanel(
                <UserDetailPanel
                    userId={selectedId}
                    onClose={() => {
                        setSelectedId(null);
                        setDetailPanel(null);
                    }}
                />
            );
        } else {
            setDetailPanel(null);
        }
        return () => setDetailPanel(null);
    }, [selectedId, setDetailPanel]);

    function updateParam(key: string, value: string | null) {
        const next = new URLSearchParams(params);
        if (!value || value === 'all') next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    }

    const total = visibleUsers.length;
    const userError = users.error instanceof Error ? users.error.message : null;
    const countError = counts.error instanceof Error ? counts.error.message : null;

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Users</h1>
                    <p>Search, filter, and manage every Producer Dashboard account.</p>
                </div>
                <div className="page-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        disabled={!visibleUsers.length}
                        onClick={() =>
                            exportCsv(
                                `users-${segment}-${new Date().toISOString().slice(0, 10)}`,
                                visibleUsers as unknown as Record<string, unknown>[]
                            )
                        }
                    >
                        <Download size={14} /> Export CSV
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => toast('Invite emailed to admin')}
                    >
                        Invite admin
                    </button>
                </div>
            </header>

            <div className="segment-tabs" role="tablist" aria-label="User segment">
                {SEGMENTS.map((s) => (
                    <button
                        key={s.id}
                        role="tab"
                        aria-selected={segment === s.id}
                        onClick={() => updateParam('segment', s.id === 'all' ? null : s.id)}
                        className="segment-tab"
                    >
                        {s.label}
                        <span className="count">
                            {counts.data ? counts.data[s.id].toLocaleString() : '—'}
                        </span>
                    </button>
                ))}
            </div>

            <div className="filter-bar">
                <label className="search">
                    <Search size={13} />
                    <input
                        type="search"
                        placeholder="Search email, name, or ID"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') updateParam('q', searchInput);
                        }}
                        onBlur={() => updateParam('q', searchInput)}
                    />
                </label>
                <select
                    className="select"
                    value={plan}
                    onChange={(e) => updateParam('plan', e.target.value)}
                    aria-label="Plan"
                >
                    <option value="all">All plans</option>
                    <option value="free">Free</option>
                    <option value="trial">Trial</option>
                    <option value="pro">Pro</option>
                    <option value="studio">Studio</option>
                    <option value="team">Team</option>
                </select>
                <select
                    className="select"
                    value={appVersion}
                    onChange={(e) => updateParam('v', e.target.value)}
                    aria-label="App version"
                >
                    <option value="all">Any version</option>
                    <option value="latest">Latest</option>
                    <option value="stale">Stale</option>
                    <option value="browser">Browser only</option>
                </select>
                <select
                    className="select"
                    value={sort}
                    onChange={(e) => updateParam('sort', e.target.value)}
                    aria-label="Sort by"
                >
                    <option value="revenue_lifetime:desc">Revenue ↓</option>
                    <option value="revenue_lifetime:asc">Revenue ↑</option>
                    <option value="gross_profit:desc">Profit ↓</option>
                    <option value="gross_profit:asc">Profit ↑</option>
                    <option value="last_active:desc">Last active</option>
                    <option value="signed_up:desc">Signed up</option>
                    <option value="tracks:desc">Tracks</option>
                    <option value="storage_gb:desc">Storage</option>
                </select>
                <span className="count">
                    Showing {total ? `1-${total}` : '0'} of{' '}
                    {counts.data?.[segment].toLocaleString() ?? '—'}
                </span>
            </div>

            {(userError || countError) && (
                <div className="alert" data-severity="danger">
                    <UsersIcon />
                    <div className="msg">
                        Failed to load users.
                        {userError ? ` ${userError}.` : ''}
                        {countError && countError !== userError
                            ? ` Segment counts: ${countError}.`
                            : ''}
                    </div>
                </div>
            )}

            <div className="card">
                {visibleUsers.length === 0 ? (
                    <div className="empty-state">
                        <UsersIcon />
                        <div className="title">No users match those filters</div>
                        <div>Try clearing the search or switching segment.</div>
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input type="checkbox" aria-label="Select all" />
                                    </th>
                                    <th>User · app</th>
                                    <th>Status</th>
                                    <th>Plan</th>
                                    <th className="numeric">Tracks</th>
                                    <th className="numeric">Storage</th>
                                    <th>Onboarding</th>
                                    <th className="numeric">Revenue</th>
                                    <th className="numeric">Profit</th>
                                    <th>Last active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleUsers.map((u) => (
                                    <tr
                                        key={u.id}
                                        aria-selected={selectedId === u.id}
                                        onClick={() => setSelectedId(u.id)}
                                    >
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${u.email}`}
                                            />
                                        </td>
                                        <td>
                                            <div className="user-cell">
                                                <div className="avatar">
                                                    {u.name
                                                        .split(' ')
                                                        .map((p) => p[0])
                                                        .slice(0, 2)
                                                        .join('')}
                                                </div>
                                                <div className="user-meta">
                                                    <span className="user-name">{u.name}</span>
                                                    <span className="user-sub">
                                                        {u.email}
                                                        <span className="version-chip">
                                                            {u.platform === 'browser'
                                                                ? 'web'
                                                                : `v${u.app_version}`}
                                                        </span>
                                                        {u.is_admin && (
                                                            <Chip tone="purple">Admin</Chip>
                                                        )}
                                                    </span>
                                                    {u.pending_legal && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <Chip tone="warning">
                                                                Terms update pending
                                                            </Chip>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <Chip tone={statusTone(u.status)} dot>
                                                {u.status}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Chip tone={planTone(u.plan)}>{u.plan}</Chip>
                                        </td>
                                        <td className="numeric">{u.tracks.toLocaleString()}</td>
                                        <td className="numeric">{fmtStorage(u.storage_gb)}</td>
                                        <td>
                                            {u.onboarding_complete ? (
                                                <Chip tone="success">complete</Chip>
                                            ) : !u.email_verified ? (
                                                <Chip tone="warning">unverified</Chip>
                                            ) : (
                                                <Chip tone="info">{`step ${u.onboarding_step}/5`}</Chip>
                                            )}
                                        </td>
                                        <td className="numeric">{fmtMoney(u.revenue_lifetime)}</td>
                                        <td
                                            className="numeric"
                                            style={{
                                                color:
                                                    u.gross_profit > 0
                                                        ? 'var(--success)'
                                                        : u.gross_profit < 0
                                                          ? 'var(--danger)'
                                                          : undefined
                                            }}
                                        >
                                            {fmtMoney(u.gross_profit)}
                                        </td>
                                        <td className="text-muted">{u.last_active}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
